import Decimal from 'decimal.js';
import { SOURCES_PRIORITY, SrcFieldValue } from '../../../../../constnats/reportConstants';
import Assert from '../../../../../utils/asserts';
import { getNumberOfDecimals } from '../../../../../utils/common';
import { normalizeValue } from '../../../../../utils/normalize';
import ObjCheck from '../../../../../utils/objCheck';
import Utils from '../../../../../utils/utils';
import { DIFF_TYPE, DIFF_TYPES_PRIORITY, DiffType } from '../../constants/peerDiff';
import { CoreValueDiff, PropDiff, ReportedAsLabelDiff, SrcDiff } from '../../utils/peerDiffUtil';
import { DIFF_DETECTOR_OPTION } from './constants';
import { DiffDetectorConfigType } from './types';

type EmptyString = '';

export function normalizeCoreValue<T>(value: T): T | EmptyString {
    return ObjCheck.isNullUndefinedEmptyOrDash(value) ? '' : value;
}

export function getEffectiveSrc(reportSrc: string, fieldSrc: string): string {
    let effectiveSrc = reportSrc;

    if(reportSrc === SrcFieldValue.MIXED) {
        if(ObjCheck.isNullUndefinedEmptyOrDash(fieldSrc)) {
            effectiveSrc = SrcFieldValue.REPORT
        }else {
            effectiveSrc = fieldSrc
        }
    }

    return effectiveSrc
}

export function getSrcPriority(src: string): number {
    return SOURCES_PRIORITY[src] ?? Infinity;
}

function getAreCoreValuesSimilar(current: string, peer: string): boolean {
    const cleanPeer = peer.replace('%', '');
    const cleanCurrent = current.replace('%', '');
    let areEqual = false;
    if (Utils.isNumber(cleanPeer) && Utils.isNumber(cleanCurrent)) {
        let peerValue = Utils.toNumber(cleanPeer);
        let currentValue = Utils.toNumber(cleanCurrent);
        const roundingPrecision = Math.min(getNumberOfDecimals(peerValue), getNumberOfDecimals(currentValue));
        areEqual =
            new Decimal(peerValue)
                .toDecimalPlaces(roundingPrecision)
                .comparedTo(new Decimal(currentValue).toDecimalPlaces(roundingPrecision)) === 0;
    } else {
        areEqual = peer === current;
    }

    return areEqual;
}

export type GetPropDiffReturnType = {
    diffType: DiffType;
    propDiff: PropDiff | null;
};


export function getSrcDiff(
    currentEffectiveSrc: string,
    peerEffectiveSrc: string,
    config: DiffDetectorConfigType
): GetPropDiffReturnType {
    let coreValueDiff: GetPropDiffReturnType;

    if (currentEffectiveSrc !== peerEffectiveSrc && !config.globalOptions.includes(DIFF_DETECTOR_OPTION.IGNORE_SRC)) {
        coreValueDiff = {
            diffType: getSrcPriority(currentEffectiveSrc) >= getSrcPriority(peerEffectiveSrc) ? DIFF_TYPE.MINOR : DIFF_TYPE.MAJOR,
            propDiff: new SrcDiff({ peerValue: peerEffectiveSrc, currentValue: currentEffectiveSrc }),
        };
    } else {
        coreValueDiff = {
            diffType: DIFF_TYPE.NO_DIFF,
            propDiff: null,
        };
    }

    Assert.notNullOrUndefined(coreValueDiff, 'getSrcDiff returned null');
    return coreValueDiff;
}


export function getCoreValueDiff(
    currentCoreValue: string | undefined,
    peerCoreValue: string | undefined,
): GetPropDiffReturnType {
    let coreValueDiff: GetPropDiffReturnType;
    
    const isMissingMatch = ObjCheck.isNullOrUndefined(currentCoreValue) !== ObjCheck.isNullOrUndefined(peerCoreValue);

    currentCoreValue = normalizeCoreValue(currentCoreValue) as string;
    peerCoreValue = normalizeCoreValue(peerCoreValue) as string;

    const areCoreValuesSimilar = getAreCoreValuesSimilar(currentCoreValue, peerCoreValue);
    if (!areCoreValuesSimilar || isMissingMatch) {
        coreValueDiff = {
            diffType: DIFF_TYPE.MAJOR,
            propDiff: new CoreValueDiff({ peerValue: peerCoreValue, currentValue: currentCoreValue }),
        };
    } else if (currentCoreValue !== peerCoreValue) {
        coreValueDiff = {
            diffType: DIFF_TYPE.MINOR,
            propDiff: new CoreValueDiff({ peerValue: peerCoreValue, currentValue: currentCoreValue }),
        };
    } else {
        coreValueDiff = {
            diffType: DIFF_TYPE.NO_DIFF,
            propDiff: null,
        };
    }
    Assert.notNullOrUndefined(coreValueDiff, 'getCoreValueDiffType returned null');
    return coreValueDiff;
}

export function getReportedAsLabelDiff(
    currentLabel: string | undefined,
    peerLabel: string | undefined,
    config: DiffDetectorConfigType
): GetPropDiffReturnType {
    let labelDiff: GetPropDiffReturnType;

    if (normalizeValue(currentLabel ?? '') === normalizeValue(peerLabel ?? '') ||
        config.globalOptions.includes(DIFF_DETECTOR_OPTION.IGNORE_REPORTED_AS_LABELS)) {
        labelDiff = { diffType: DIFF_TYPE.NO_DIFF, propDiff: null };
    } else {
        labelDiff = {
            diffType: ObjCheck.isNullUndefinedOrEmpty(currentLabel) ? DIFF_TYPE.MAJOR : DIFF_TYPE.MINOR,
            propDiff: new ReportedAsLabelDiff({ peerValue: peerLabel ?? '', currentValue: currentLabel ?? "" }),
        };
    }
    Assert.notNullOrUndefined(labelDiff, 'getLabelDiff returned null');
    return labelDiff;
}

export function getHightestPriorityDiffType(diffTypes: DiffType[]): DiffType {
    return diffTypes.reduce((min, current) => {
        return getDiffTypePriority(current) <= getDiffTypePriority(min) ? current : min;
    });
}

function getDiffTypePriority(diffType: DiffType): number {
    return DIFF_TYPES_PRIORITY[diffType] ?? Infinity;
}
